import { useCallback, useMemo } from "react";
import { MenuItem } from "@szhsin/react-menu";
import { useHotkeys } from "react-hotkeys-hook";

import cn from "@utils/cn";
import { isArrayNotEmpty, getIconProps } from "@utils";
import Icon from "@elements/Icon";

const CLASS_NAMES = cn(`
  group
  flex
  self-center
  justify-between
  gap-x-2
  px-4
  py-[0.7rem]
  text-base
  font-medium
  outline-hidden
  cursor-pointer

  bg-menu-item-bg
  text-menu-item-text
  [--icon-background-color:var(--color-menu-item-bg)]
  [--icon-transition-duration:0s]

  hover:bg-menu-item-bg-hover
  hover:text-menu-item-text-hover
  hover:[--icon-background-color:var(--color-menu-item-bg-hover)]

  focus:bg-menu-item-bg-focus
  focus:text-menu-item-text-hover
  focus:[--icon-background-color:var(--color-menu-item-bg-focus)]

  active:bg-menu-item-bg-active
  active:text-menu-item-text-active
  active:[--icon-background-color:var(--color-menu-item-bg-active)]
`);

const DISABLED_CLASS_NAMES = cn(`
  cursor-not-allowed

  bg-menu-item-disabled-bg
  text-menu-item-disabled-text
  [--icon-background-color:var(--color-menu-item-disabled-bg)]

  hover:bg-menu-item-disabled-bg
  hover:text-menu-item-disabled-text

  focus:bg-menu-item-disabled-bg
  focus:text-menu-item-disabled-text

  active:bg-menu-item-disabled-bg
  active:text-menu-item-disabled-text
`);

const KBD_CLASS_NAMES = cn(`
  flex
  gap-x-0.5
  px-2
  py-0.5
  text-xs
  font-semibold
  rounded-md
  border

  bg-gallery-100
  text-gallery-400
  border-gallery-300
  shadow-xs
`);

const DISABLED_TEXT_CLASS_NAMES = cn(`
  text-[0.8rem]
  leading-5

  text-[#AAAAAA]
`);

const HELP_TEXT_CLASS_NAMES = cn(`
  text-[0.8rem]
  leading-5

  text-[#888888]

  group-hover:text-[#777777]

  group-focus:text-[#444444]
`);

export default function ActionMenuBaseItem(props) {
  const {
    // type,
    className,
    label = {},
    hotkeys,
    onClick,

    helpText,
    helpTextClassName,
    isDisabled,
    disabledText,
    disabledTextClassName,
  } = props;

  const {
    text,
    icon,
  } = label;

  const iconProps = getIconProps(icon, {
    fixedWidth: true,
  });

  const handleClick = useCallback((event) => {
    if (isDisabled) return;

    onClick(event);
  }, [isDisabled, onClick]);

  /**************************************************************************************************/

  const hasHotkey = isArrayNotEmpty(hotkeys);

  const hotkeyDefinition = useMemo(() => {
    if (!hasHotkey) return null;

    return hotkeys[0]
      .replace("meta", "⌘")
      .replace("ctrl", "^")
      .replace("shift", "⇧")
      .replace("option", "⌥")
      .replace("alt", "⌥")
      .replace("period", ".")
      .toUpperCase();
  }, [hotkeys, hasHotkey]);

  useHotkeys(hotkeys || [], handleClick, {
    enabled: hasHotkey && !isDisabled,
    enableOnFormTags: false,
    enableOnContentEditable: false,
    preventDefault: true,
  }, [handleClick]);

  /**************************************************************************************************/

  const classNames = cn(CLASS_NAMES, className, {
    [DISABLED_CLASS_NAMES]: isDisabled,
  });

  const helpTextClassNames = cn(HELP_TEXT_CLASS_NAMES, helpTextClassName);
  const disabledTextClassNames = cn(DISABLED_TEXT_CLASS_NAMES, disabledTextClassName);

  return (
    <MenuItem
      className={classNames}
      onClick={handleClick}
      disabled={isDisabled}
    >
      <span className="flex items-start gap-x-3">
        {iconProps && (
          <span className="mt-[0.15rem] mb-0 text-sm align-middle transition-all duration-250 ease-in-out">
            <Icon {...iconProps} />
          </span>
        )}

        <span className="align-middle">
          {text}

          {isDisabled && disabledText && (
            <div className={disabledTextClassNames}>
              {disabledText}
            </div>
          )}

          {!isDisabled && helpText && (
            <div className={helpTextClassNames}>
              {helpText}
            </div>
          )}
        </span>
      </span>

      <span>
        {hasHotkey && (
          <span className={KBD_CLASS_NAMES}>
            {hotkeyDefinition}
          </span>
        )}
      </span>
    </MenuItem>
  );
};
